import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setSelectedTrack, setSelectedPart } from '../store/appSlice';
import { Track } from '../types';
import DarkSelect from './DarkSelect';

interface TrackSelectorProps {
  tracks: Track[];
}

const TrackSelector: React.FC<TrackSelectorProps> = ({ tracks }) => {
  const dispatch = useAppDispatch();
  const { selectedTrackId, selectedPartIndex } = useAppSelector((state) => state.app.backingTrack);
  const [selectedTrack, setSelectedTrackState] = useState<Track | null>(null);

  useEffect(() => {
    if (selectedTrackId && tracks.length > 0) {
      const track = tracks.find((t) => t.id === selectedTrackId);
      setSelectedTrackState(track || null);
      // If switching to a track with no remembered part selection, default to
      // the first part so the user immediately has something to play.
      if (track && selectedPartIndex === null && track.metadata.parts.length > 0) {
        dispatch(setSelectedPart(0));
      }
    } else {
      setSelectedTrackState(null);
    }
  }, [selectedTrackId, tracks, selectedPartIndex, dispatch]);

  const handleTrackChange = (value: string) => {
    dispatch(setSelectedTrack(value || null));
  };

  const handlePartChange = (value: string) => {
    const partIndex = value ? parseInt(value, 10) : null;
    dispatch(setSelectedPart(partIndex));
  };

  return (
    <div className="mb-4">
      <Form.Group className="mb-3">
        <Form.Label>Select Track</Form.Label>
        <DarkSelect
          value={selectedTrackId || ''}
          onChange={handleTrackChange}
          placeholder="-- Select a track --"
          options={tracks.map((track) => ({ value: track.id, label: track.name }))}
        />
      </Form.Group>

      {selectedTrack && (
        <Form.Group className="mb-3">
          <Form.Label>Select Part</Form.Label>
          <DarkSelect
            value={selectedPartIndex !== null ? String(selectedPartIndex) : ''}
            onChange={handlePartChange}
            placeholder="-- Select a part --"
            options={selectedTrack.metadata.parts.map((part, index) => ({
              value: String(index),
              label: `${part.title} - ${part.bpmOverride || selectedTrack.metadata.bpm} BPM`,
            }))}
          />
        </Form.Group>
      )}
    </div>
  );
};

export default TrackSelector;
